// IndexedDB read/write transactions serialize mutations across tabs. Network work
// never runs inside these transactions. A batch belongs to one tab while open.
export const BATCH_MAX_RECORDS = 400;
const DB_NAME = 'inventory-operation-outbox-v1';
let database;

function openDatabase() {
  if (!database) {
    database = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('batches', { keyPath: 'key' }).createIndex('scope', 'scope');
        request.result.createObjectStore('records', { keyPath: 'key' });
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); database = undefined; };
        db.onclose = () => { database = undefined; };
        resolve(db);
      };
      request.onerror = () => reject(request.error);
    }).catch((error) => { database = undefined; throw error; });
  }
  return database;
}

// Check actual local read/write access, not just whether the API exists.
// The probe never creates an operation or contacts Firestore.
export async function checkInventoryStorage() {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('records', 'readwrite');
    const records = tx.objectStore('records');
    const key = `storage-probe-${Date.now()}-${Math.random()}`;
    let failure;
    records.put({ key, value: 'available' });
    const read = records.get(key);
    read.onsuccess = () => {
      if (read.result?.value !== 'available') {
        failure = new Error('Local storage verification failed');
        tx.abort();
        return;
      }
      records.delete(key);
    };
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(failure || tx.error || new Error('Local storage verification aborted'));
    tx.onerror = () => { /* onabort owns rejection */ };
  });
}

export function createOutbox(scope, changed = () => {}) {
  const keyFor = (id) => JSON.stringify([scope, id]);
  const transact = async (write, action, id) => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['batches', 'records'], write ? 'readwrite' : 'readonly');
      const table = tx.objectStore('batches');
      const records = tx.objectStore('records');
      const request = id ? table.get(keyFor(id)) : table.index('scope').getAll(scope);
      let result;
      let dirty = false;
      let failure;
      request.onsuccess = () => {
        try {
          result = action(id ? [request.result].filter(Boolean) : request.result, {
            put: (batch) => { table.put(batch); dirty = true; },
            remove: (key) => { table.delete(key); dirty = true; },
            putRecord: (record) => records.put({ ...record, key: keyFor(record.id) }),
            removeRecord: (recordId) => records.delete(keyFor(recordId)),
          });
        } catch (error) { failure = error; tx.abort(); }
      };
      tx.oncomplete = () => {
        resolve(result);
        // A notification failure must never turn a committed local save into a lost promise.
        if (dirty) { try { changed(); } catch (_) { /* the journal is already durable */ } }
      };
      tx.onabort = () => reject(failure || tx.error || new Error('Local outbox transaction aborted'));
      tx.onerror = () => { /* onabort owns rejection */ };
    });
  };
  return {
    list: () => transact(false, (batches) => batches),
    get: (id) => transact(false, (batches) => batches[0], id),
    record: async (id) => {
      const db = await openDatabase();
      return new Promise((resolve, reject) => {
        const request = db.transaction('records', 'readonly').objectStore('records').get(keyFor(id));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    },
    append: ({
      inventoryId, ownerId, batchId, recordId, payload,
    }) => transact(true, (batches, table) => {
      let batch = batches.find((b) => b.inventoryId === inventoryId && b.ownerId === ownerId && !b.closed);
      if (!batch) {
        batch = {
          key: keyFor(batchId), scope, id: batchId, inventoryId, ownerId, closed: false, records: [],
        };
      }
      const record = { id: recordId, data: { ...payload, updateBatchId: batch.id } };
      batch.records.push({ id: recordId });
      table.putRecord(record);
      if (batch.records.length >= BATCH_MAX_RECORDS) batch.closed = true;
      table.put(batch);
      return { batch, record };
    }),
    close: (id) => transact(true, (batches, table) => {
      const batch = batches.find((b) => b.id === id);
      if (batch && !batch.closed) { batch.closed = true; table.put(batch); }
      return batch;
    }, id),
    remove: (id) => transact(true, (batches, table) => {
      const batch = batches.find((b) => b.id === id);
      if (batch) {
        table.remove(batch.key);
        batch.records.forEach((record) => table.removeRecord(record.id));
      }
    }, id),
    importLegacy: (inventoryId, pending) => transact(true, (batches, table) => {
      const existing = batches.find((b) => b.id === pending.batchId);
      if (existing) {
        if (existing.inventoryId !== inventoryId) throw new Error('Legacy batch inventory mismatch');
        if (pending.recordIds.some((id) => !existing.records.some((r) => r.id === id))) {
          throw new Error('Legacy batch changed in another tab; close the old app tab before retrying');
        }
        return;
      }
      if (!pending.batchId || !Array.isArray(pending.recordIds) || !pending.recordIds.length
        || pending.recordIds.length > BATCH_MAX_RECORDS) throw new Error('Invalid legacy pending batch');
      table.put({
        key: keyFor(pending.batchId),
        scope,
        id: pending.batchId,
        inventoryId,
        ownerId: 'legacy',
        closed: true,
        records: [...new Set(pending.recordIds)].map((id) => ({ id })),
      });
    }),
  };
}
